import React from 'react';
import { IAnnouncement } from 'announcements/types';
import styled from 'styled-components';
import { Button, ButtonText } from 'components/Button';
import { Modal, ModalContent, ModalHeader, ModalText, ModalTitle } from 'components/Modal';
import { H3, Text } from 'components/Typography';
import { lineHeight, theme, toRem } from 'design';

interface IProps {
  announcement: IAnnouncement;
  isActive: boolean;
  dataCy?: string;
  onClose: () => void;
  onDismiss?: () => void;
}

const HeroImage = styled.figure<{ maxWidth?: number; maxHeight?: number }>`
  display: flex;
  justify-content: center;
  width: 100%;

  img {
    display: block;
    width: 100%;
    max-width: ${({ maxWidth }) => (maxWidth ? toRem(maxWidth) : '100%')};
    ${({ maxHeight }) => (maxHeight ? `max-height: ${toRem(maxHeight)};` : '')}
    height: auto;
    object-fit: contain;
    border-radius: ${toRem(11)};
  }
`;

const HeroCaption = styled.figcaption`
  margin-top: ${toRem(theme.space.sm)};
  font-family: ${theme.font.family.euclid};
  font-weight: ${theme.font.weight.regular};
  font-size: ${toRem(theme.font.size.xs)};
  line-height: ${lineHeight(theme.font.size.xs)};
  color: ${theme.color.neutral[700]};
`;

const List = styled.div`
  display: flex;
  flex-flow: column nowrap;
  justify-content: flex-start;
  align-items: stretch;
  width: 100%;
  gap: ${toRem(theme.space.lg)};
  margin-top: ${toRem(theme.space.lg)};
`;

const Item = styled.div`
  display: flex;
  flex-flow: column nowrap;
  width: 100%;
  padding: 0 0 ${toRem(theme.space.lg)} 0;
  gap: ${toRem(theme.space.md)};
`;

const ItemSection = styled.div`
  display: flex;
  flex-flow: column nowrap;
  width: 100%;
  margin-top: ${toRem(theme.space.sm)};
  gap: ${toRem(theme.space.xs)};

  h3 {
    font-size: ${toRem(theme.font.size.md)};
    line-height: ${lineHeight(theme.font.size.md)};
  }

  p {
    color: ${theme.color.neutral[500]};
  }
`;

const ItemImage = styled.figure<{ maxWidth?: number; maxHeight?: number }>`
  width: 100%;
  margin: ${toRem(theme.space.md)} 0 0;

  img {
    display: block;
    width: 100%;
    max-width: ${({ maxWidth }) => (maxWidth ? toRem(maxWidth) : '100%')};
    ${({ maxHeight }) => (maxHeight ? `max-height: ${toRem(maxHeight)};` : '')}
    height: auto;
    object-fit: contain;
    border-radius: ${toRem(11)};
  }
`;

const ItemCaption = styled.figcaption`
  margin-top: ${toRem(theme.space.sm)};
  font-family: ${theme.font.family.euclid};
  font-weight: ${theme.font.weight.regular};
  font-size: ${toRem(theme.font.size.xs)};
  line-height: ${lineHeight(theme.font.size.xs)};
  color: ${theme.color.neutral[700]};
`;

const ItemBullets = styled.ul`
  margin-top: ${toRem(theme.space.xs)};
  padding-left: ${toRem(theme.space.lg)};
  list-style-type: disc;
`;

const ItemBullet = styled.li`
  font-family: ${theme.font.family.euclid};
  font-weight: ${theme.font.weight.regular};
  font-size: ${toRem(theme.font.size.sm)};
  line-height: ${lineHeight(theme.font.size.sm)};
  color: ${theme.color.neutral[500]};
`;

const ItemActions = styled.div`
  display: flex;
  flex-flow: row wrap;
  justify-content: flex-start;
  align-items: center;
  width: 100%;
  gap: ${toRem(theme.space.md)};
  margin-top: ${toRem(theme.space.md)};
`;

const DismissFooter = styled.div`
  display: flex;
  justify-content: center;
  width: 100%;
  margin-top: ${toRem(theme.space.lg)};
`;

const ExternalLink = styled.a`
  font-family: ${theme.font.family.euclid};
  font-weight: ${theme.font.weight.regular};
  font-size: ${toRem(theme.font.size.sm)};
  line-height: ${lineHeight(theme.font.size.sm)};
  color: ${theme.color.primary[500]};
  text-decoration-line: underline;

  &:visited,
  &:active {
    color: ${theme.color.primary[500]};
  }
`;

const AnnouncementModal: React.FC<IProps> = ({ announcement, isActive, onClose, onDismiss, dataCy = 'announcement-modal' }: IProps) => (
  <Modal isActive={isActive} onClose={onClose} maxWidth={640} dataCy={dataCy}>
    <ModalHeader>
      <ModalTitle>{announcement.title}</ModalTitle>
      <ModalText marginTop={8}>{announcement.description}</ModalText>
    </ModalHeader>
    <ModalContent textAlign="left">
      <List>
        <Item data-cy={`${dataCy}-item`}>
          {announcement.heroImage && (
            <HeroImage maxWidth={announcement.heroImage.maxWidth} maxHeight={announcement.heroImage.maxHeight}>
              <img src={announcement.heroImage.src} alt={announcement.heroImage.alt} />
              {announcement.heroImage.caption && <HeroCaption>{announcement.heroImage.caption}</HeroCaption>}
            </HeroImage>
          )}
          {announcement.body.items.map((item, idx) => (
            <ItemSection key={`${announcement.id}-${idx}`} data-cy={`${dataCy}-item-body-item`}>
              {item.title && <H3>{item.title}</H3>}
              {item.description && <Text size="small">{item.description}</Text>}
              {item.image && (
                <ItemImage maxWidth={item.image.maxWidth} maxHeight={item.image.maxHeight}>
                  <img src={item.image.src} alt={item.image.alt} />
                  {item.image.caption && <ItemCaption>{item.image.caption}</ItemCaption>}
                </ItemImage>
              )}
              {item.bullets && item.bullets.length > 0 && (
                <ItemBullets>
                  {item.bullets.map((bullet, index) => (
                    <ItemBullet key={`bullet-${idx}-${index}`}>{bullet}</ItemBullet>
                  ))}
                </ItemBullets>
              )}
              {item.ctaHref && (
                <ItemActions>
                  <ExternalLink href={item.ctaHref} target="_blank" rel="noopener noreferrer">
                    {item.ctaLabel ?? 'Learn more'}
                  </ExternalLink>
                </ItemActions>
              )}
            </ItemSection>
          ))}
          {(announcement.ctaTo || announcement.ctaHref) && (
            <ItemActions>
              {announcement.ctaTo && <ButtonText label={announcement.ctaLabel ?? 'Learn more'} to={announcement.ctaTo} />}
              {announcement.ctaHref && (
                <ExternalLink href={announcement.ctaHref} target="_blank" rel="noopener noreferrer">
                  {announcement.ctaLabel ?? 'Learn more'}
                </ExternalLink>
              )}
            </ItemActions>
          )}
        </Item>
      </List>
      {onDismiss && (
        <DismissFooter>
          <Button label="Dismiss Announcement" onClick={onDismiss} data-cy={`${dataCy}-dismiss-btn`} />
        </DismissFooter>
      )}
    </ModalContent>
  </Modal>
);

export default AnnouncementModal;
